import * as THREE from 'three';
import type { Game } from './Game';
import { Entity, type EntityCtx } from './Entity';
import { Explosion } from './entities/Explosion';
import { PlayerPlane } from './entities/PlayerPlane';
import { EnemyPlane } from './entities/EnemyPlane';
import { Bullet } from './entities/Bullet';
import { useStore } from '../state/store';

/** One offbeat, self-contained set-piece — the UFO encounter's little siblings. The director runs
 *  at most one at a time, on a cooldown, picking a weighted-random eligible event. Every event is
 *  responsible for cleaning up any global state it touched (lights, fog, flags, meshes). */
interface GameEvent {
  readonly id: string;
  /** Relative random weight when rolling. */
  readonly weight: number;
  /** Can this fire right now? (conditions on wave / squadron / villages / boss / altitude). */
  eligible(g: Game): boolean;
  /** Set up state + spawn. */
  start(g: Game, ctx: EntityCtx): void;
  /** Advance one frame; return true when finished. */
  update(g: Game, ctx: EntityCtx): boolean;
  /** Restore anything global (called on finish AND on abort/restart — must be idempotent). */
  cleanup(g: Game): void;
}

const hint = (t: string) => useStore.getState().setControlHint(t);
const card = (title: string, sub: string, color: string) => useStore.getState().showEventCard(title, sub, color);

export class RandomEventDirector {
  private active: GameEvent | null = null;
  private cooldown = 240;   // seconds before the first possible event (~4 min in)
  private checkTimer = 0;
  private readonly events: GameEvent[] = [
    new Cavalry(), new Comet(), new Turncoat(), new Airdrop(), new Gremlins(),
    new BloodDawn(), new FogBank(), new Eclipse(), new StElmo(),
    new GhostSquadron(), new Balloon(), new Flock(), new Leviathan(),
    new AceDuel(), new FlyingCircus(), new Truce(),
  ];

  update(g: Game, ctx: EntityCtx) {
    const st = useStore.getState();
    if (st.phase !== 'playing') { this.abort(g); return; }
    if (this.active) {
      let done = false;
      try { done = this.active.update(g, ctx); } catch (e) { console.error('[event]', this.active.id, e); done = true; }
      if (done) this.finish(g);
      return;
    }
    if (g.isUfoActive() || st.tutorial) return; // don't overlap the UFO or intrude on the tutorial
    this.cooldown -= ctx.dt;
    if (this.cooldown > 0) return;
    this.checkTimer -= ctx.dt;
    if (this.checkTimer > 0) return;
    this.checkTimer = 15;                // once off cooldown, roll every 15s...
    if (Math.random() > 0.35) return;    // ...at 35% → a little jitter on top of the long cooldown
    const pool = this.events.filter((e) => e.eligible(g));
    if (!pool.length) return;
    const total = pool.reduce((s, e) => s + e.weight, 0);
    let r = Math.random() * total;
    let pick = pool[pool.length - 1];
    for (const e of pool) { r -= e.weight; if (r <= 0) { pick = e; break; } }
    try { pick.start(g, ctx); this.active = pick; }
    catch (err) { console.error('[event start]', pick.id, err); try { pick.cleanup(g); } catch { /* */ } }
  }

  private finish(g: Game) {
    try { this.active!.cleanup(g); } catch (e) { console.error('[event cleanup]', e); }
    this.active = null;
    this.cooldown = 330 + Math.random() * 300; // 5.5–10.5 min between events → ~3 across a game
    this.checkTimer = 0;
  }
  private abort(g: Game) { if (this.active) { try { this.active.cleanup(g); } catch { /* */ } this.active = null; } }
  reset(g: Game) { this.abort(g); this.cooldown = 240; this.checkTimer = 0; }

  /** Debug/verification: force-start an event by id (bypasses cooldown + eligibility). */
  force(g: Game, ctx: EntityCtx, id: string): boolean {
    if (this.active) this.finish(g);
    const e = this.events.find((x) => x.id === id);
    if (!e) return false;
    try { e.start(g, ctx); this.active = e; return true; } catch (err) { console.error('[event force]', id, err); return false; }
  }
  activeId(): string | null { return this.active?.id ?? null; }
}

// --- helpers ------------------------------------------------------------------------------------
const wave = () => useStore.getState().wave;
const villages = () => useStore.getState().villagesAlive;
function disposeGroup(scene: THREE.Scene, grp: THREE.Group) {
  scene.remove(grp);
  grp.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose()); else mat?.dispose?.();
  });
}
/** Lerp a Color toward a target by rate*dt (per-frame ease). */
function lerpColor(c: THREE.Color, hex: number, k: number) { c.lerp(_tmpCol.setHex(hex), k); }
const _tmpCol = new THREE.Color();
const _UP = new THREE.Vector3(0, 1, 0);
const _sv1 = new THREE.Vector3();
const _sv2 = new THREE.Vector3();
function approach(cur: number, target: number, k: number) { return cur + (target - cur) * k; }

// ================================================================================================
// BATCH A — comeback / chaos
// ================================================================================================

/** The Cavalry — down to your last kite, a fresh flight roars in over the ridge. */
class Cavalry implements GameEvent {
  readonly id = 'cavalry'; readonly weight = 3;
  private t = 0;
  eligible(g: Game) { return g.alliedFighterCount() <= 1 && villages() > 0; }
  start(g: Game) {
    this.t = 0;
    const a = Math.random() * Math.PI * 2;
    const n = 2 + (Math.random() < 0.5 ? 1 : 0);
    for (let i = 0; i < n; i++) {
      const aa = a + (i - (n - 1) / 2) * 0.18;
      g.spawnAlliedFighter(new THREE.Vector3(Math.cos(aa) * 2200, 150 + i * 25, Math.sin(aa) * 2200));
    }
    g.audio.playWaveCleared();
    card('THE CAVALRY', 'Reinforcements over the ridge', '#7be77b');
  }
  update(_g: Game, ctx: EntityCtx) { this.t += ctx.dt; return this.t > 2; }
  cleanup() { /* fighters persist */ }
}

/** Falling Star — a meteor tears down and craters the battlefield (friend or foe). */
class Comet implements GameEvent {
  readonly id = 'comet'; readonly weight = 3;
  private mesh: THREE.Group | null = null;
  private target = new THREE.Vector3();
  private pos = new THREE.Vector3();
  private vel = new THREE.Vector3();
  eligible() { return true; }
  start(g: Game) {
    // Aim at an enemy-tank cluster if there is one, else a random spot in the play area.
    const tanks = g.entities.filter((e) => e.kind === 'tank' && !e.dead);
    if (tanks.length) this.target.copy(tanks[Math.floor(Math.random() * tanks.length)].position);
    else { const a = Math.random() * Math.PI * 2, r = 600 + Math.random() * 2200; this.target.set(Math.cos(a) * r, 0, Math.sin(a) * r); }
    this.target.y = g.gameScene.heightAt(this.target.x, this.target.z);
    // Streak in from high up, off to one side, on a ballistic line.
    const off = new THREE.Vector3((Math.random() - 0.5) * 1600, 0, (Math.random() - 0.5) * 1600);
    this.pos.copy(this.target).add(off); this.pos.y = 1400;
    this.vel.copy(this.target).sub(this.pos).normalize().multiplyScalar(900); // u/s
    this.mesh = makeMeteor();
    this.mesh.position.copy(this.pos);
    g.scene.add(this.mesh);
    g.audio.playMeteor();
    card('FALLING STAR', 'Take cover', '#ff8a3a');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    if (!this.mesh) return true;
    this.pos.addScaledVector(this.vel, ctx.dt);
    this.mesh.position.copy(this.pos);
    this.mesh.rotation.x += ctx.dt * 6; this.mesh.rotation.z += ctx.dt * 4;
    if (this.pos.y <= this.target.y + 4) {
      const at = this.target.clone(); at.y += 4;
      new Explosion(at, 26, 0.8, 'fire').addTo(ctx);
      new Explosion(at, 20, 3, 'smoke').setDrift(new THREE.Vector3(0, 6, 0)).addTo(ctx);
      g.audio.playBoom(1.4);
      g.crashImpact(ctx, this.target, 95, 42); // impartial: fells trees, craters buildings, hurts any unit
      disposeGroup(g.scene, this.mesh); this.mesh = null;
      return true;
    }
    return false;
  }
  cleanup(g: Game) { if (this.mesh) { disposeGroup(g.scene, this.mesh); this.mesh = null; } }
}

/** Turncoat Ace — an enemy waggles its wings and defects to your side. */
class Turncoat implements GameEvent {
  readonly id = 'turncoat'; readonly weight = 2;
  private enemy: EnemyPlane | null = null;
  private t = 0; private swapped = false;
  eligible(g: Game) { return g.entities.some((e) => e instanceof EnemyPlane && !e.dead && !e.passive); }
  start(g: Game) {
    this.t = 0; this.swapped = false;
    const cands = g.entities.filter((e) => e instanceof EnemyPlane && !e.dead && !e.passive) as EnemyPlane[];
    this.enemy = cands[Math.floor(Math.random() * cands.length)] ?? null;
    card('DEFECTION', 'A pilot changes sides', '#8ac0ff');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    this.t += ctx.dt;
    if (this.enemy) this.enemy.group.rotation.z += ctx.dt * 6; // waggle
    if (!this.swapped && this.t > 1.1) {
      this.swapped = true;
      if (this.enemy && !this.enemy.dead) {
        const p = this.enemy.position.clone();
        this.enemy.dead = true;
        g.spawnAlliedFighter(p); // joins as a standard wingman (allied fighters carry their own livery)
        g.audio.playReinforcement();
      }
    }
    return this.t > 1.4;
  }
  cleanup() { this.enemy = null; }
}

/** Munitions Airdrop — fly through a parachuted crate for a fire-rate overcharge. */
class Airdrop implements GameEvent {
  readonly id = 'airdrop'; readonly weight = 2;
  private crate: THREE.Group | null = null;
  private phase: 'fall' | 'buff' = 'fall';
  private t = 0; private plane: PlayerPlane | null = null;
  eligible(g: Game) { return g.controlled instanceof PlayerPlane; }
  start(g: Game) {
    this.phase = 'fall'; this.t = 0; this.plane = null;
    const p = g.controlled.position;
    this.crate = makeCrate();
    this.crate.position.set(p.x + (Math.random() - 0.5) * 200, p.y + 380, p.z + (Math.random() - 0.5) * 200);
    g.scene.add(this.crate);
    card('SUPPLY DROP', 'Fly through the crate', '#ffcf5a');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    if (this.phase === 'fall') {
      if (!this.crate) return true;
      this.t += ctx.dt;
      this.crate.position.y -= 55 * ctx.dt;
      this.crate.rotation.y += ctx.dt * 0.6;
      const gy = g.gameScene.heightAt(this.crate.position.x, this.crate.position.z);
      const pl = g.controlled;
      if (pl instanceof PlayerPlane && pl.position.distanceTo(this.crate.position) < 55) {
        this.plane = pl; pl.fireMult = 0.5;
        useStore.getState().setEventTint('rgba(255,180,60,0.9)');
        g.audio.playChime();
        hint('⚡ OVERCHARGE — GUNS HOT');
        disposeGroup(g.scene, this.crate); this.crate = null;
        this.phase = 'buff'; this.t = 0;
        return false;
      }
      if (this.crate.position.y <= gy + 6 || this.t > 22) {
        // Landed uncollected — a ground crew "recovers" it. No buff, no penalty.
        disposeGroup(g.scene, this.crate); this.crate = null;
        return true;
      }
      return false;
    }
    // buff phase
    this.t += ctx.dt;
    return this.t > 15;
  }
  cleanup(g: Game) {
    if (this.crate) { disposeGroup(g.scene, this.crate); this.crate = null; }
    if (this.plane) this.plane.fireMult = 1;
    useStore.getState().setEventTint('');
  }
}

/** Clockwork Gremlins — the guns cough and the engine skips for a spell. */
class Gremlins implements GameEvent {
  readonly id = 'gremlins'; readonly weight = 2;
  private t = 0; private rattle = 0; private plane: PlayerPlane | null = null;
  eligible(g: Game) { return g.controlled instanceof PlayerPlane && villages() > 1; }
  start(g: Game) {
    this.t = 0; this.rattle = 0;
    this.plane = g.controlled as PlayerPlane; this.plane.jammed = true;
    useStore.getState().setEventTint('rgba(90,150,70,0.85)');
    g.audio.playRattle();
    card('GREMLINS', "Something's loose in the works", '#7fd06a');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    this.t += ctx.dt; this.rattle -= ctx.dt;
    if (this.rattle <= 0) { g.audio.playRattle(); this.rattle = 1.2 + Math.random(); }
    return this.t > 9;
  }
  cleanup() { if (this.plane) this.plane.jammed = false; useStore.getState().setEventTint(''); }
}

// ================================================================================================
// BATCH B — atmospheric (save originals, ramp in → hold → ramp out; cleanup snaps back)
// ================================================================================================

abstract class AtmosphericEvent implements GameEvent {
  abstract readonly id: string; abstract readonly weight: number;
  protected phase: 'in' | 'hold' | 'out' = 'in';
  protected t = 0; protected holdFor = 30;
  // A multiply HUD wash — the ONLY reliable way to recolour the whole view, since the Sky is a
  // shader dome untouched by dirLight/fog/background. '' = rely on fog alone (whitening).
  protected tint = '';
  private orig: { lc: THREE.Color; li: number; fc: THREE.Color; fn: number; ff: number; bg: THREE.Color | null } | null = null;
  eligible(g: Game) { return !g.bossActive(); }
  protected save(g: Game) {
    const s = g.gameScene; const fog = s.root.fog as THREE.Fog;
    const bg = s.root.background instanceof THREE.Color ? s.root.background.clone() : null;
    this.orig = { lc: s.dirLight.color.clone(), li: s.dirLight.intensity, fc: fog.color.clone(), fn: fog.near, ff: fog.far, bg };
  }
  protected restore(g: Game) {
    if (!this.orig) return;
    const s = g.gameScene; const fog = s.root.fog as THREE.Fog;
    s.dirLight.color.copy(this.orig.lc); s.dirLight.intensity = this.orig.li;
    fog.color.copy(this.orig.fc); fog.near = this.orig.fn; fog.far = this.orig.ff;
    if (this.orig.bg && s.root.background instanceof THREE.Color) s.root.background.copy(this.orig.bg);
  }
  abstract applyTarget(g: Game, k: number): void; // ramp toward the effect by ease k
  abstract announce(g: Game): void;
  start(g: Game) { this.save(g); this.phase = 'in'; this.t = 0; if (this.tint) useStore.getState().setEventTint(this.tint); this.announce(g); }
  update(g: Game, ctx: EntityCtx): boolean {
    const k = 1 - Math.exp(-ctx.dt / 0.6);
    if (this.phase === 'in') { this.applyTarget(g, k); this.t += ctx.dt; if (this.t > 4) { this.phase = 'hold'; this.t = 0; } }
    else if (this.phase === 'hold') { this.applyTarget(g, k); this.t += ctx.dt; if (this.t > this.holdFor) { this.phase = 'out'; this.t = 0; useStore.getState().setEventTint(''); } }
    else { // ramp back toward originals
      if (this.orig) { const s = g.gameScene; const fog = s.root.fog as THREE.Fog;
        s.dirLight.color.lerp(this.orig.lc, k); s.dirLight.intensity = approach(s.dirLight.intensity, this.orig.li, k);
        fog.color.lerp(this.orig.fc, k); fog.near = approach(fog.near, this.orig.fn, k); fog.far = approach(fog.far, this.orig.ff, k);
        if (this.orig.bg && s.root.background instanceof THREE.Color) s.root.background.lerp(this.orig.bg, k);
      }
      this.t += ctx.dt; if (this.t > 4) return true;
    }
    return false;
  }
  cleanup(g: Game) { this.restore(g); useStore.getState().setEventTint(''); }
}

/** Blood-Red Dawn — the low sun turns the front rust-red. */
class BloodDawn extends AtmosphericEvent {
  readonly id = 'blooddawn'; readonly weight = 2;
  constructor() { super(); this.holdFor = 35; this.tint = 'rgba(205,85,45,0.42)'; }
  start(g: Game) { super.start(g); g.enemyFireScale = 0.6; } // the enemy has the sun — they fire faster
  applyTarget(g: Game, k: number) {
    const s = g.gameScene; const fog = s.root.fog as THREE.Fog;
    lerpColor(s.dirLight.color, 0xff5a2a, k); s.dirLight.intensity = approach(s.dirLight.intensity, 1.3, k);
    lerpColor(fog.color, 0xc06438, k);
    if (s.root.background instanceof THREE.Color) lerpColor(s.root.background, 0xc27048, k);
  }
  cleanup(g: Game) { super.cleanup(g); g.enemyFireScale = 1; }
  announce() { card('BLOOD-RED DAWN', 'The sun is theirs', '#ff6a3a'); }
}

/** Rolling Fog Bank — visibility collapses; fly on radar. */
class FogBank extends AtmosphericEvent {
  readonly id = 'fogbank'; readonly weight = 2;
  constructor() { super(); this.holdFor = 30; }
  applyTarget(g: Game, k: number) {
    const s = g.gameScene; const fog = s.root.fog as THREE.Fog;
    fog.near = approach(fog.near, 200, k); fog.far = approach(fog.far, 1500, k);
    lerpColor(fog.color, 0xb9c2cc, k);
  }
  announce() { card('FOG BANK', 'Fly on instruments', '#cfd8e2'); }
}

/** The Long Dark — the sun guts out; fight by muzzle-flash. */
class Eclipse extends AtmosphericEvent {
  readonly id = 'eclipse'; readonly weight = 1;
  constructor() { super(); this.holdFor = 45; this.tint = 'rgba(26,30,74,0.72)'; }
  start(g: Game) { super.start(g); g.audio.playCreatureCall(); useStore.getState().setHideMarks(true); } // fight by radar + muzzle-flash
  cleanup(g: Game) { super.cleanup(g); useStore.getState().setHideMarks(false); }
  applyTarget(g: Game, k: number) {
    const s = g.gameScene; const fog = s.root.fog as THREE.Fog;
    lerpColor(s.dirLight.color, 0x2a2f55, k); s.dirLight.intensity = approach(s.dirLight.intensity, 0.18, k);
    lerpColor(fog.color, 0x0e1226, k); fog.far = approach(fog.far, 6000, k);
    if (s.root.background instanceof THREE.Color) lerpColor(s.root.background, 0x0a0e1e, k);
  }
  announce() { card('THE LONG DARK', 'Fight by muzzle-flash', '#9aa0e0'); }
}

/** St. Elmo's Fire — blue witch-fire crawls the wings; kills read "charged". */
class StElmo extends AtmosphericEvent {
  readonly id = 'stelmo'; readonly weight = 2;
  private spark = 0; private killsAt = 0;
  constructor() { super(); this.holdFor = 24; this.tint = 'rgba(150,180,220,0.2)'; }
  start(g: Game) { super.start(g); this.killsAt = useStore.getState().kills; }
  applyTarget(g: Game, k: number) {
    const s = g.gameScene; const fog = s.root.fog as THREE.Fog;
    fog.near = approach(fog.near, 500, k); fog.far = approach(fog.far, 3500, k);
    lerpColor(fog.color, 0x8fb0d8, k);
  }
  update(g: Game, ctx: EntityCtx): boolean {
    if (this.phase !== 'out') {
      // CHARGED GUNS: the aether supercharges your reload while the storm is up...
      if (g.controlled instanceof PlayerPlane) g.controlled.fireMult = 0.6;
      // ...and every kill scored during the storm pays a charged bounty.
      const st = useStore.getState();
      if (st.kills > this.killsAt) { st.addScore((st.kills - this.killsAt) * 300); this.killsAt = st.kills; }
      // Blue witch-fire crawling the wings.
      this.spark -= ctx.dt;
      if (this.spark <= 0) {
        this.spark = 0.1;
        const p = g.controlled.position;
        const off = new THREE.Vector3((Math.random() - 0.5) * 9, (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 9);
        new Explosion(p.clone().add(off), 3, 0.25, 'shield').addTo(ctx);
      }
    }
    return super.update(g, ctx);
  }
  cleanup(g: Game) { super.cleanup(g); for (const e of g.entities) if (e instanceof PlayerPlane) e.fireMult = 1; }
  announce() { card("ST. ELMO'S FIRE", 'Guns charged', '#9fd0ff'); }
}

// ================================================================================================
// BATCH C — encounters / entities
// ================================================================================================

/** Ghost Squadron — spectral biplanes fade in; down them all for a payout, or they vanish. */
class GhostSquadron implements GameEvent {
  readonly id = 'ghosts'; readonly weight = 1;
  private ghosts: EnemyPlane[] = [];
  private t = 0; private paid = false;
  private savedFar = 0;
  eligible(g: Game) { return !g.bossActive() && g.controlled instanceof PlayerPlane; }
  start(g: Game) {
    this.t = 0; this.paid = false; this.ghosts = [];
    const fog = g.gameScene.root.fog as THREE.Fog; this.savedFar = fog.far; fog.far = Math.min(fog.far, 3000);
    const p = g.controlled.position;
    for (let i = 0; i < 3; i++) {
      const a = Math.atan2(p.z, p.x) + Math.PI + (i - 1) * 0.25; // ahead, loosely
      const gp = new THREE.Vector3(p.x + Math.cos(a) * 500, p.y + 40 + i * 20, p.z + Math.sin(a) * 500);
      const e = new EnemyPlane(gp, 'standard');
      e.makeGhostly();
      g.add(e); this.ghosts.push(e);
    }
    g.audio.playEventOmen();
    card('THE LOST FLIGHT', 'Down them all', '#a8e0ff');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    this.t += ctx.dt;
    const alive = this.ghosts.filter((e) => !e.dead);
    if (!this.paid && alive.length === 0 && this.ghosts.length) {
      this.paid = true; useStore.getState().addScore(1500); g.audio.playChime(); hint('☁ THE LOST FLIGHT — VANQUISHED (+1500)');
      return true;
    }
    if (this.t > 18) { for (const e of alive) e.dead = true; g.audio.playChime(); return true; } // fade away
    return false;
  }
  cleanup(g: Game) {
    const fog = g.gameScene.root.fog as THREE.Fog; if (this.savedFar) fog.far = Math.max(fog.far, this.savedFar);
    for (const e of this.ghosts) if (!e.dead) e.dead = true;
    this.ghosts = [];
  }
}

/** Observation Balloon — escort the drifting recon balloon home for a free wingman. */
class Balloon implements GameEvent {
  readonly id = 'balloon'; readonly weight = 2;
  private balloon: BalloonEntity | null = null;
  private t = 0;
  eligible(g: Game) { return villages() > 0 && !g.bossActive(); }
  start(g: Game) {
    this.t = 0;
    const a = Math.random() * Math.PI * 2;
    const pos = new THREE.Vector3(Math.cos(a) * 2600, 120, Math.sin(a) * 2600);
    this.balloon = new BalloonEntity(pos);
    g.add(this.balloon);
    card('RECON BALLOON', 'See it safely home', '#ded39c');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    this.t += ctx.dt;
    const b = this.balloon;
    if (!b || b.dead) { g.audio.playHydrogenRoar(); hint('🎈 BALLOON LOST'); this.balloon = null; return true; }
    if (Math.hypot(b.position.x, b.position.z) < 450) {
      b.dead = true; this.balloon = null;
      const fa = Math.random() * Math.PI * 2;
      g.spawnAlliedFighter(new THREE.Vector3(Math.cos(fa) * 1800, 160, Math.sin(fa) * 1800));
      g.audio.playChime(); hint('🎈 BALLOON HOME — CREW SENDS A WINGMAN');
      return true;
    }
    if (this.t > 60) { if (b) b.dead = true; this.balloon = null; return true; }
    return false;
  }
  cleanup(g: Game) { if (this.balloon && !this.balloon.dead) { this.balloon.dead = true; } this.balloon = null; void g; }
}

/** Migrating Flock — a skein of geese crosses your path; mind the propeller. */
class Flock implements GameEvent {
  readonly id = 'flock'; readonly weight = 3;
  private grp: THREE.Group | null = null;
  private vel = new THREE.Vector3();
  private t = 0; private struck = false;
  eligible(g: Game) { return !g.bossActive() && g.controlled instanceof PlayerPlane; }
  start(g: Game) {
    this.t = 0; this.struck = false;
    const p = g.controlled.position;
    const a = Math.random() * Math.PI * 2;
    this.grp = makeFlock();
    this.grp.position.set(p.x + Math.cos(a) * 900, Math.max(60, p.y + 30), p.z + Math.sin(a) * 900);
    this.vel.set(-Math.cos(a), 0, -Math.sin(a)).multiplyScalar(70); // crossing toward/past the player
    g.scene.add(this.grp);
    card('MIGRATING FLOCK', 'Mind the propeller', '#b3a98f');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    if (!this.grp) return true;
    this.t += ctx.dt;
    this.grp.position.addScaledVector(this.vel, ctx.dt);
    // gentle wing flap
    this.grp.children.forEach((c, i) => { c.rotation.z = Math.sin(this.t * 8 + i) * 0.4; });
    const pl = g.controlled;
    if (!this.struck && pl instanceof PlayerPlane && pl.position.distanceTo(this.grp.position) < 40) {
      this.struck = true;
      pl.damage(2, ctx);
      new Explosion(this.grp.position.clone(), 6, 0.4, 'smoke').addTo(ctx);
      hint('🦆 BIRD STRIKE');
    }
    return this.t > 22;
  }
  cleanup(g: Game) { if (this.grp) { disposeGroup(g.scene, this.grp); this.grp = null; } }
}

/** The Leviathan — a vast aether-beast crosses the high haze. A downable white-whale bounty:
 *  non-hostile, enormous HP, and a legend-tier score windfall if you can bring it down before it
 *  leaves. Self-managed bullet hits (never enters the enemy list, so it can't block a wave clear). */
class Leviathan implements GameEvent {
  readonly id = 'leviathan'; readonly weight = 1;
  private grp: THREE.Group | null = null;
  private eye: THREE.Mesh | null = null;
  private vel = new THREE.Vector3();
  private t = 0; private hp = 0; private hpMax = 90; private deadAt = -1; private hurt = 0;
  eligible(g: Game) { return g.controlled.position.y > 150 && !g.bossActive(); } // only when you're up high
  start(g: Game) {
    this.t = 0; this.hp = this.hpMax; this.deadAt = -1; this.hurt = 0;
    const a = Math.random() * Math.PI * 2;
    this.grp = makeLeviathan();
    this.eye = this.grp.userData.eye as THREE.Mesh;
    this.grp.position.set(Math.cos(a) * 3000, 820, Math.sin(a) * 3000);
    this.vel.set(-Math.cos(a), 0, -Math.sin(a)).multiplyScalar(60);
    this.grp.lookAt(this.grp.position.clone().add(this.vel));
    g.scene.add(this.grp);
    g.audio.playCreatureCall();
    card('THE LEVIATHAN', 'A legend on the wing', '#ffcc55');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    if (!this.grp) return true;
    this.t += ctx.dt;
    if (this.deadAt < 0) {
      this.grp.position.addScaledVector(this.vel, ctx.dt);
      this.grp.position.y = 820 + Math.sin(this.t * 0.4) * 40;
      if (this.t > 9 && this.t < 9.3) g.audio.playCreatureCall();
      // Self-managed hits: swallow player rounds inside its bulk and take damage.
      const r2 = 150 * 150; const c = this.grp.position;
      for (const e of g.entities) {
        if (e.dead || !(e instanceof Bullet) || e.owner !== 'player') continue;
        if (e.position.distanceToSquared(c) <= r2) {
          e.dead = true; this.hp -= Math.max(1, e.damageAmount); this.hurt = 0.12;
          new Explosion(e.position.clone(), 6, 0.3, 'fire').addTo(ctx);
        }
      }
      // Flash the eye/body when struck; pulse the eye with a heartbeat otherwise.
      const glow = this.hurt > 0 ? 3 : 1 + Math.sin(this.t * 2) * 0.4;
      const em = (this.eye?.material as THREE.MeshBasicMaterial | undefined);
      if (em) em.color.setRGB(1, this.hurt > 0 ? 0.3 : 0.8, this.hurt > 0 ? 0.2 : 0.27).multiplyScalar(glow / 2 + 0.5);
      this.hurt = Math.max(0, this.hurt - ctx.dt);
      if (this.hp <= 0) { this.kill(g, ctx); }
      if (this.t > 80) return true; // it escapes into the haze
      return false;
    }
    // Death throes: it lists, sinks, and breaks apart, then vanishes.
    this.grp.position.y -= 55 * ctx.dt; this.grp.rotation.z += ctx.dt * 0.5;
    if (Math.random() < 0.35) new Explosion(this.grp.position.clone().add(new THREE.Vector3((Math.random() - 0.5) * 220, (Math.random() - 0.5) * 90, (Math.random() - 0.5) * 220)), 26, 1, 'fire').addTo(ctx);
    return this.t > this.deadAt + 3.2;
  }
  private kill(g: Game, ctx: EntityCtx) {
    this.deadAt = this.t;
    const c = this.grp!.position.clone();
    for (let i = 0; i < 10; i++) new Explosion(c.clone().add(new THREE.Vector3((Math.random() - 0.5) * 240, (Math.random() - 0.5) * 90, (Math.random() - 0.5) * 240)), 34, 1.1, 'fire').addTo(ctx);
    new Explosion(c, 60, 4, 'smoke').setDrift(new THREE.Vector3(0, 8, 0)).addTo(ctx);
    g.audio.playHydrogenRoar(); g.audio.playBoom(1.8);
    useStore.getState().addScore(6000);
    hint('~ THE LEVIATHAN FALLS — LEGEND (+6000) ~');
  }
  cleanup(g: Game) { if (this.grp) { disposeGroup(g.scene, this.grp); this.grp = null; this.eye = null; } }
}

// ================================================================================================
// BATCH D — twists / cameos
// ================================================================================================

/** The Ace's Duel — a lone ace calls you out. Down it in the window for glory, or it breaks off. */
class AceDuel implements GameEvent {
  readonly id = 'aceduel'; readonly weight = 1;
  private ace: EnemyPlane | null = null; private t = 0; private done = false;
  eligible(g: Game) { return !g.bossActive() && wave() >= 3; }
  start(g: Game) {
    this.t = 0; this.done = false;
    const p = g.controlled.position; const a = Math.random() * Math.PI * 2;
    const pos = new THREE.Vector3(p.x + Math.cos(a) * 700, Math.max(80, p.y), p.z + Math.sin(a) * 700);
    this.ace = new EnemyPlane(pos, 'ace');
    this.ace.setLivery(0x1a1a1a); // a black knight
    g.add(this.ace);
    g.audio.playBossSpawn();
    card("THE ACE'S DUEL", 'Just you and me, Baron', '#ff5a5a');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    this.t += ctx.dt;
    if (this.ace && this.ace.dead && !this.done) { this.done = true; useStore.getState().addScore(2500); g.audio.playChime(); hint('⚔ DUEL WON — RESPECT (+2500)'); return true; }
    if (this.t > 26 && !this.done) {
      this.done = true;
      // Ignore penalty: the ace makes a contemptuous strafing pass on a village, then escapes.
      if (this.ace && !this.ace.dead) {
        const vs = g.gameScene.villages.filter((v) => !v.destroyed);
        vs.sort((a, b) => a.position.distanceToSquared(this.ace!.position) - b.position.distanceToSquared(this.ace!.position));
        if (vs[0]) g.crashImpact(ctx, vs[0].position.clone(), 70, 6, this.ace);
        g.audio.playBoom(0.9);
        this.ace.dead = true; // vanishes into the haze
        hint('⚔ IGNORED — THE ACE STRAFES A VILLAGE AND ESCAPES');
      } else { hint('⚔ THE ACE BREAKS OFF'); }
      return true;
    }
    return false;
  }
  cleanup() { this.ace = null; }
}

/** The Flying Circus — a garish squadron shows off, then rolls in to fight. */
class FlyingCircus implements GameEvent {
  readonly id = 'circus'; readonly weight = 1;
  private t = 0; private paid = false;
  private show = 0; private roll = 0;
  private center = new THREE.Vector3(); private fwd = new THREE.Vector3();
  private troupe: EnemyPlane[] = [];
  private static readonly LIVERIES = [0xff3b30, 0xffd23b, 0x37a3ff, 0x9b4dff, 0x2ec16b];
  eligible(g: Game) { return !g.bossActive() && wave() >= 2; }
  start(g: Game) {
    this.t = 0; this.paid = false; this.show = 0; this.roll = 0; this.troupe = [];
    const p = g.controlled.position; const a = Math.atan2(p.z, p.x) + Math.PI;
    this.center.set(Math.cos(a) * 1400, Math.max(140, p.y + 50), Math.sin(a) * 1400);
    this.fwd.copy(p).sub(this.center).setY(0).normalize(); // sweep toward the player
    for (let i = 0; i < 4; i++) {
      const e = new EnemyPlane(this.center.clone(), 'scout');
      e.setLivery(FlyingCircus.LIVERIES[i % FlyingCircus.LIVERIES.length]);
      e.passive = true; // held for the aerobatic show; released to combat after
      g.add(e); this.troupe.push(e);
    }
    // The ringmaster (lead) flies gold and takes some killing.
    this.troupe[0].setLivery(0xffd23b); this.troupe[0].hp = 8;
    g.audio.playBossSpawn();
    card('THE FLYING CIRCUS', 'A show, then a fight', '#ff4d8d');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    // --- THE SHOW: a synchronised barrel-roll pass in tight formation (~4.5s) ---
    if (this.show < 4.5) {
      this.show += ctx.dt; this.roll += ctx.dt * 3.6;
      this.center.addScaledVector(this.fwd, 65 * ctx.dt); this.center.y += 5 * ctx.dt;
      const right = _sv1.copy(this.fwd).cross(_UP).normalize();
      const look = _sv2.copy(this.center).add(this.fwd);
      this.troupe.forEach((e, i) => {
        if (e.dead) return;
        e.group.position.copy(this.center).addScaledVector(right, (i - 1.5) * 44);
        e.group.lookAt(look);
        e.group.rotateZ(this.roll); // unison barrel roll
      });
      if (this.show >= 4.5) { for (const e of this.troupe) e.passive = false; hint('🎪 …AND NOW THE FIGHT'); } // release to combat
      return false;
    }
    // --- Combat: clear the troupe for the bounty ---
    this.t += ctx.dt;
    const alive = this.troupe.filter((e) => !e.dead).length;
    if (!this.paid && this.troupe.length && alive === 0) { this.paid = true; useStore.getState().addScore(2000); g.audio.playChime(); hint('🎪 TROUPE DOWNED (+2000)'); return true; }
    return this.t > 32; // otherwise they persist as normal combat
  }
  cleanup(g: Game) { for (const e of this.troupe) e.passive = false; this.troupe = []; void g; }
}

/** The Whistle — a cease-fire falls over the whole field for twenty eerie seconds. */
class Truce implements GameEvent {
  readonly id = 'truce'; readonly weight = 1;
  private t = 0; private ended = false;
  eligible(g: Game) { return !g.bossActive() && villages() > 0; }
  start(g: Game) {
    this.t = 0; this.ended = false;
    g.ceaseFire = true;
    g.audio.playWhistle();
    card('CEASE FIRE', 'Hold your fire', '#f6faff');
  }
  update(g: Game, ctx: EntityCtx): boolean {
    this.t += ctx.dt;
    if (!this.ended && this.t > 20) { this.ended = true; g.ceaseFire = false; g.audio.playWhistle(); hint('✋ RESUME FIRE'); }
    return this.t > 21;
  }
  cleanup(g: Game) { g.ceaseFire = false; }
}

// ================================================================================================
// Procedural prop meshes + the Balloon entity
// ================================================================================================

function makeMeteor(): THREE.Group {
  const g = new THREE.Group();
  const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(10, 0), new THREE.MeshStandardMaterial({ color: 0x2a2320, roughness: 1, emissive: 0xff5a1a, emissiveIntensity: 1.6, flatShading: true }));
  g.add(rock);
  const glow = new THREE.Mesh(new THREE.SphereGeometry(16, 16, 12), new THREE.MeshBasicMaterial({ color: 0xff8a3a, transparent: true, opacity: 0.35 }));
  g.add(glow);
  // a stubby trailing cone
  const trail = new THREE.Mesh(new THREE.ConeGeometry(9, 46, 12), new THREE.MeshBasicMaterial({ color: 0xffb057, transparent: true, opacity: 0.4 }));
  trail.rotation.x = -Math.PI / 2; trail.position.z = 24;
  g.add(trail);
  return g;
}

function makeCrate(): THREE.Group {
  const g = new THREE.Group();
  const box = new THREE.Mesh(new THREE.BoxGeometry(9, 8, 9), new THREE.MeshStandardMaterial({ color: 0x6a4a2a, roughness: 0.9, flatShading: true }));
  g.add(box);
  for (const b of [-1, 1]) { const strap = new THREE.Mesh(new THREE.BoxGeometry(9.4, 1.2, 1.4), new THREE.MeshStandardMaterial({ color: 0x2a2a2a })); strap.position.z = b * 2.6; g.add(strap); }
  const canopy = new THREE.Mesh(new THREE.SphereGeometry(11, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: 0xdcdcd0, roughness: 0.9, side: THREE.DoubleSide }));
  canopy.position.y = 16; g.add(canopy);
  const lineMat = new THREE.LineBasicMaterial({ color: 0xbbbbbb });
  for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2; const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(Math.cos(a) * 4, 4, Math.sin(a) * 4), new THREE.Vector3(Math.cos(a) * 8, 15, Math.sin(a) * 8)]); g.add(new THREE.Line(geo, lineMat)); }
  return g;
}

function makeFlock(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color: 0x2a2620 });
  for (let i = 0; i < 14; i++) {
    const bird = new THREE.Group();
    for (const s of [-1, 1]) { const w = new THREE.Mesh(new THREE.BoxGeometry(6, 0.4, 1.6), mat); w.position.x = s * 3; w.rotation.z = s * 0.4; bird.add(w); }
    bird.position.set((Math.random() - 0.5) * 90, (Math.random() - 0.5) * 30, (Math.random() - 0.5) * 90);
    bird.scale.setScalar(0.7 + Math.random() * 0.6);
    g.add(bird);
  }
  return g;
}

function makeLeviathan(): THREE.Group {
  const g = new THREE.Group();
  const hull = new THREE.MeshStandardMaterial({ color: 0x3a4048, roughness: 0.85, metalness: 0.2, flatShading: true });
  const brass = new THREE.MeshStandardMaterial({ color: 0xb5892f, roughness: 0.4, metalness: 0.8 });
  const body = new THREE.Mesh(new THREE.SphereGeometry(70, 20, 14), hull);
  body.scale.set(1, 0.7, 3.4); g.add(body); // long whale-ish gasbag body
  for (let i = -2; i <= 2; i++) { const rib = new THREE.Mesh(new THREE.TorusGeometry(48, 3, 8, 20), brass); rib.rotation.y = Math.PI / 2; rib.scale.y = 0.7; rib.position.z = i * 55; g.add(rib); }
  const eye = new THREE.Mesh(new THREE.SphereGeometry(11, 12, 10), new THREE.MeshBasicMaterial({ color: 0xffcc44 }));
  eye.position.set(26, 6, -210); g.add(eye);
  const eye2 = eye.clone(); eye2.position.x = -26; g.add(eye2);
  g.userData.eye = eye;
  const fin = new THREE.Mesh(new THREE.ConeGeometry(40, 90, 4), hull); fin.rotation.x = Math.PI / 2; fin.position.z = 230; fin.scale.set(1, 0.3, 1); g.add(fin);
  return g;
}

/** A drifting friendly recon balloon (kind 'ally' so enemy fire + turret targeting can threaten it). */
class BalloonEntity extends Entity {
  private mesh: THREE.Group;
  constructor(pos: THREE.Vector3) {
    super('ally');
    this.friendly = true; this.controllable = false; this.radius = 22; this.hp = 10;
    this.unitLabel = 'BALLOON';
    this.group.position.copy(pos);
    this.mesh = new THREE.Group();
    const bag = new THREE.Mesh(new THREE.SphereGeometry(20, 18, 14), new THREE.MeshStandardMaterial({ color: 0x9a9a86, roughness: 0.9, flatShading: true }));
    bag.scale.set(1, 1.25, 1); bag.position.y = 8; this.mesh.add(bag);
    const basket = new THREE.Mesh(new THREE.BoxGeometry(8, 6, 8), new THREE.MeshStandardMaterial({ color: 0x6a4a2a, roughness: 0.95 }));
    basket.position.y = -18; this.mesh.add(basket);
    const fin = new THREE.Mesh(new THREE.ConeGeometry(6, 16, 4), new THREE.MeshStandardMaterial({ color: 0x8a8a76 })); fin.rotation.x = Math.PI / 2; fin.position.set(0, 8, 20); this.mesh.add(fin);
    this.group.add(this.mesh);
  }
  update(ctx: EntityCtx): void {
    // Drift toward the map centre at a slow, steady clip; bob gently.
    const to = new THREE.Vector3(-this.group.position.x, 0, -this.group.position.z);
    const d = to.length(); if (d > 1) to.multiplyScalar(1 / d);
    this.group.position.addScaledVector(to, 55 * ctx.dt);
    this.group.position.y = 120 + Math.sin(ctx.now * 0.5) * 6;
  }
  hpFrac(): number { return THREE.MathUtils.clamp(this.hp / 10, 0, 1); }
  damage(amount: number, ctx: EntityCtx): void { this.hp -= amount; this.lastDamageAt = ctx.now; if (this.hp <= 0) this.die(ctx); }
  die(ctx: EntityCtx): void {
    if (this.dead) return; this.dead = true;
    new Explosion(this.group.position.clone(), 24, 0.8, 'fire').addTo(ctx);
    new Explosion(this.group.position.clone(), 18, 2.5, 'smoke').setDrift(new THREE.Vector3(0, 5, 0)).addTo(ctx);
    ctx.game.audio.playHydrogenRoar();
  }
}
